// src/services/DB.js
class DB {
  constructor() {
    this.dbName = "CompanyRegistrationDB";
    this.storeName = "formData";
    this.version = 1;
    this.db = null;
  }

  // Membuka atau membuat database
  openDatabase() {
    return new Promise((resolve, reject) => {
      // Check if IndexedDB is supported
      if (!window.indexedDB) {
        reject(new Error("IndexedDB is not supported in this browser"));
        return;
      }

      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: "id" });

          // Create indexes for better querying
          store.createIndex("savedAt", "savedAt", { unique: false });
          store.createIndex("type", "type", { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;

        // Handle database version change
        this.db.onversionchange = () => {
          this.db.close();
          console.log("Database version changed, please reload the page.");
        };

        resolve(this.db);
      };

      request.onerror = (event) => {
        console.error("Database error:", event.target.error);
        reject(new Error(`Database error: ${event.target.error}`));
      };

      request.onblocked = () => {
        console.warn("Database upgrade blocked. Please close other tabs.");
      };
    });
  }

  // Menyimpan data dengan validasi
  save(id, data) {
    return this.openDatabase().then(() => {
      return new Promise((resolve, reject) => {
        if (!id || !data) {
          reject(new Error("ID and data are required"));
          return;
        }

        try {
          const transaction = this.db.transaction(
            [this.storeName],
            "readwrite"
          );
          const store = transaction.objectStore(this.storeName);

          // Add metadata to the data
          const dataToSave = {
            id: id,
            data: data,
            savedAt: new Date().toISOString(),
            type: "companyRegistration",
          };

          const request = store.put(dataToSave);

          request.onsuccess = () => {
            console.log(`Data saved successfully with ID: ${id}`);
            resolve({
              success: true,
              message: "Data saved successfully!",
              id: id,
              savedAt: dataToSave.savedAt,
            });
          };

          request.onerror = (event) => {
            console.error("Save error:", event.target.error);
            reject(new Error(`Save error: ${event.target.error}`));
          };

          transaction.oncomplete = () => {
            console.log("Transaction completed successfully");
          };

          transaction.onerror = (event) => {
            console.error("Transaction error:", event.target.error);
            reject(new Error(`Transaction error: ${event.target.error}`));
          };
        } catch (error) {
          reject(new Error(`Save operation failed: ${error.message}`));
        }
      });
    });
  }

  // Mengambil data dengan error handling yang lebih baik
  get(id) {
    return this.openDatabase().then(() => {
      return new Promise((resolve, reject) => {
        if (!id) {
          reject(new Error("ID is required"));
          return;
        }

        try {
          const transaction = this.db.transaction([this.storeName], "readonly");
          const store = transaction.objectStore(this.storeName);
          const request = store.get(id);

          request.onsuccess = () => {
            const result = request.result;
            if (result) {
              console.log(`Data retrieved successfully for ID: ${id}`);
              resolve(result.data);
            } else {
              console.log(`No data found for ID: ${id}`);
              resolve(null);
            }
          };

          request.onerror = (event) => {
            console.error("Get error:", event.target.error);
            reject(new Error(`Get error: ${event.target.error}`));
          };
        } catch (error) {
          reject(new Error(`Get operation failed: ${error.message}`));
        }
      });
    });
  }

  // Mengambil semua data
  getAll() {
    return this.openDatabase().then(() => {
      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db.transaction([this.storeName], "readonly");
          const store = transaction.objectStore(this.storeName);
          const request = store.getAll();

          request.onsuccess = () => {
            const results = request.result || [];
            console.log(`Retrieved ${results.length} records`);
            resolve(
              results.map((item) => ({
                id: item.id,
                data: item.data,
                savedAt: item.savedAt,
                type: item.type,
              }))
            );
          };

          request.onerror = (event) => {
            console.error("GetAll error:", event.target.error);
            reject(new Error(`GetAll error: ${event.target.error}`));
          };
        } catch (error) {
          reject(new Error(`GetAll operation failed: ${error.message}`));
        }
      });
    });
  }

  // IMPROVED: Method remove() dengan error handling yang lebih baik
  remove(id) {
    console.log(`remove() called with ID: ${id}`);

    if (!id) {
      return Promise.resolve({
        success: true,
        message: "No ID provided, skipping removal",
        id: null,
      });
    }

    // Check if data exists first
    return this.exists(id)
      .then((dataExists) => {
        if (!dataExists) {
          console.log(`Data with ID ${id} does not exist, skipping removal`);
          return {
            success: true,
            message: `Data with ID ${id} does not exist, no action needed`,
            id: id,
          };
        }

        // Data exists, proceed with deletion
        return this.delete(id);
      })
      .catch((error) => {
        console.warn(
          `Error checking/removing data with ID ${id}:`,
          error.message
        );
        // Return success to prevent blocking other operations
        return {
          success: true,
          message: `Error during removal but continuing: ${error.message}`,
          id: id,
          warning: true,
        };
      });
  }

  // IMPROVED: Method delete() dengan better error handling
  delete(id) {
    return this.openDatabase()
      .then(() => {
        return new Promise((resolve, reject) => {
          if (!id) {
            resolve({
              success: true,
              message: "No ID provided, skipping deletion",
              id: null,
            });
            return;
          }

          try {
            const transaction = this.db.transaction(
              [this.storeName],
              "readwrite"
            );
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(id);

            request.onsuccess = () => {
              console.log(`Data deleted successfully for ID: ${id}`);
              resolve({
                success: true,
                message: "Data deleted successfully!",
                id: id,
              });
            };

            request.onerror = (event) => {
              console.error("Delete error:", event.target.error);
              // Don't reject, just resolve with error info
              resolve({
                success: false,
                message: `Delete error: ${event.target.error}`,
                id: id,
                error: event.target.error,
              });
            };

            transaction.onerror = (event) => {
              console.error("Transaction error:", event.target.error);
              resolve({
                success: false,
                message: `Transaction error: ${event.target.error}`,
                id: id,
                error: event.target.error,
              });
            };
          } catch (error) {
            console.error("Delete operation failed:", error.message);
            resolve({
              success: false,
              message: `Delete operation failed: ${error.message}`,
              id: id,
              error: error,
            });
          }
        });
      })
      .catch((error) => {
        console.error("Database open failed:", error.message);
        // Return resolved promise instead of rejected
        return {
          success: false,
          message: `Database open failed: ${error.message}`,
          id: id,
          error: error,
        };
      });
  }

  // Membersihkan semua data
  clear() {
    return this.openDatabase().then(() => {
      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db.transaction(
            [this.storeName],
            "readwrite"
          );
          const store = transaction.objectStore(this.storeName);
          const request = store.clear();

          request.onsuccess = () => {
            console.log("All data cleared successfully");
            resolve({
              success: true,
              message: "All data cleared successfully!",
            });
          };

          request.onerror = (event) => {
            console.error("Clear error:", event.target.error);
            reject(new Error(`Clear error: ${event.target.error}`));
          };
        } catch (error) {
          reject(new Error(`Clear operation failed: ${error.message}`));
        }
      });
    });
  }

  // Mengecek apakah data ada
  exists(id) {
    return this.get(id)
      .then((data) => {
        return data !== null;
      })
      .catch(() => {
        return false;
      });
  }

  // ADDED: Method untuk update data secara partial
  // Berguna untuk mengupdate bagian tertentu dari data tanpa mengganti seluruhnya
  update(id, partialData) {
    return this.get(id).then((existingData) => {
      if (!existingData) {
        throw new Error(`No data found with ID: ${id}`);
      }

      // Merge existing data with partial update
      const updatedData = { ...existingData, ...partialData };
      return this.save(id, updatedData);
    });
  }

  // ADDED: Method untuk mendapatkan data dengan default value
  // Jika data tidak ditemukan, return default value instead of null
  getWithDefault(id, defaultValue = {}) {
    return this.get(id).then((data) => {
      return data !== null ? data : defaultValue;
    });
  }

  // ADDED: Method untuk backup data
  // Export semua data dalam format JSON
  exportData() {
    return this.getAll().then((allData) => {
      const exportData = {
        exportDate: new Date().toISOString(),
        version: this.version,
        data: allData,
      };
      return JSON.stringify(exportData, null, 2);
    });
  }

  // ADDED: Method untuk restore data dari backup
  // Import data dari JSON backup
  importData(jsonData) {
    return new Promise((resolve, reject) => {
      try {
        const importData = JSON.parse(jsonData);

        if (!importData.data || !Array.isArray(importData.data)) {
          throw new Error("Invalid backup data format");
        }

        // Clear existing data first
        this.clear()
          .then(() => {
            // Import each record
            const importPromises = importData.data.map((item) => {
              return this.save(item.id, item.data);
            });

            Promise.all(importPromises)
              .then(() => {
                console.log(
                  `Successfully imported ${importData.data.length} records`
                );
                resolve({
                  success: true,
                  message: `Successfully imported ${importData.data.length} records`,
                  importedCount: importData.data.length,
                });
              })
              .catch(reject);
          })
          .catch(reject);
      } catch (error) {
        reject(new Error(`Import failed: ${error.message}`));
      }
    });
  }

  // Mendapatkan informasi database
  getInfo() {
    return this.openDatabase().then(() => {
      return {
        name: this.dbName,
        version: this.version,
        storeName: this.storeName,
        isOpen: this.db !== null,
      };
    });
  }

  // ADDED: Method untuk mendapatkan statistik data
  getStats() {
    return this.getAll().then((allData) => {
      const stats = {
        totalRecords: allData.length,
        types: {},
        oldestRecord: null,
        newestRecord: null,
      };

      allData.forEach((item) => {
        // Count by type
        const type = item.type || "unknown";
        stats.types[type] = (stats.types[type] || 0) + 1;

        // Find oldest and newest records
        const savedAt = new Date(item.savedAt);
        if (!stats.oldestRecord || savedAt < new Date(stats.oldestRecord)) {
          stats.oldestRecord = item.savedAt;
        }
        if (!stats.newestRecord || savedAt > new Date(stats.newestRecord)) {
          stats.newestRecord = item.savedAt;
        }
      });

      return stats;
    });
  }

  // Menutup koneksi database
  close() {
    if (this.db) {
      this.db.close();
      this.db = null;
      console.log("Database connection closed");
    }
  }
}

// Export singleton instance
export default new DB();
